

import React, { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, Headphones, ShieldCheck } from "lucide-react";

import { AuthAPI } from "../api/AuthAPI";

export default function CustomerSupport() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    quantity: "",
  });

  const [loading, setLoading] = useState(false);

  const [success, setSuccess] = useState("");

  const [error, setError] = useState("");

  /* ================= VALIDATION ================= */

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const isValidPhone = (phone) => /^[0-9]{10}$/.test(phone);

  /* ================= INPUT CHANGE ================= */

  function handleChange(e) {
    const { name, value } = e.target;

    if (name === "phone") {
      if (!/^\d*$/.test(value)) return;

      if (value.length > 10) return;
    }

    setForm({
      ...form,
      [name]: value,
    });
  }

  /* ================= SUBMIT ================= */

  async function handleSubmit(e) {
    e.preventDefault();

    setError("");

    setSuccess("");

    /* VALIDATION */

    if (!isValidEmail(form.email)) {
      setError("Please enter a valid email address");
      return;
    }

    if (!isValidPhone(form.phone)) {
      setError("Phone number must be exactly 10 digits");
      return;
    }

    setLoading(true);

    try {
      await AuthAPI.createcontantus({
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: form.quantity,
      });

      setSuccess("Request submitted successfully.");

      setForm({
        name: "",
        email: "",
        phone: "",
        quantity: "",
      });
    } catch {
      setError("Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <section
      className="
        relative
        overflow-hidden

        py-16 md:py-24

        bg-gradient-to-b
        from-[#fff8fc]
        via-white
        to-[#f8fbff]

        font-poppins
      "
    >
      {/* BG BLUR */}

      <div
        className="
          absolute
          top-0
          left-0

          w-72
          h-72

          bg-pink-100/40

          rounded-full

          blur-3xl
        "
      />

      <div
        className="
          absolute
          bottom-0
          right-0

          w-72
          h-72

          bg-blue-100/40

          rounded-full

          blur-3xl
        "
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        <div
          className="
            grid
            grid-cols-1
            lg:grid-cols-2

            gap-8 lg:gap-12

            items-stretch
          "
        >
          {/* ================= LEFT CARD ================= */}

          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="
              relative

              overflow-hidden

              rounded-[32px]

              min-h-[420px]

              p-8 md:p-10

              flex
              flex-col
              justify-between

              bg-gradient-to-br
              from-[#1A237E]
              via-[#4F46E5]
              to-[#EC4899]

              text-white

              shadow-[0_20px_60px_rgba(91,45,168,0.20)]
            "
          >
            {/* BG IMAGE */}

            <div
              className="
                absolute
                inset-0

                opacity-10

                bg-cover
                bg-center
              "
              style={{
                backgroundImage: "url('/im.webp')",
              }}
            />

            {/* TOP CONTENT */}

            <div className="relative z-10">
              {/* TAG */}

              <div
                className="
                  inline-flex
                  items-center

                  px-4
                  py-2

                  rounded-full

                  bg-white/15
                  backdrop-blur-md

                  text-xs
                  font-semibold
                  tracking-[2px]
                  uppercase

                  mb-6
                "
              >
                Customer Support
              </div>

              {/* TITLE */}

              <h2
                className="
                  text-[32px]
                  md:text-[42px]

                  leading-[1.1]

                  font-bold

                  mb-5
                "
              >
                We’re Here
                <br />
                To Help You
              </h2>

              {/* TEXT */}

              <p
                className="
                  text-white/80

                  text-[15px]
                  md:text-[16px]

                  leading-[1.9]

                  max-w-md
                "
              >
                Need assistance with orders, products or support? Our team is
                always ready to help you with quick and reliable guidance.
              </p>
            </div>

            {/* SUPPORT INFO */}

            <div className="relative z-10 space-y-4 mt-10">
              {/* PHONE */}

              <div
                className="
                  flex
                  items-center
                  gap-4

                  rounded-2xl

                  bg-white/10
                  backdrop-blur-md

                  p-4
                "
              >
                <div
                  className="
                    w-12
                    h-12

                    rounded-xl

                    bg-white/15

                    flex
                    items-center
                    justify-center
                  "
                >
                  <Phone size={20} />
                </div>

                <div>
                  <p className="text-sm text-white/70">Call Us</p>

                  <p className="font-semibold">+91 97043 59405</p>
                </div>
              </div>

              {/* EMAIL */}

              <div
                className="
                  flex
                  items-center
                  gap-4

                  rounded-2xl

                  bg-white/10
                  backdrop-blur-md

                  p-4
                "
              >
                <div
                  className="
                    w-12
                    h-12

                    rounded-xl

                    bg-white/15

                    flex
                    items-center
                    justify-center
                  "
                >
                  <Mail size={20} />
                </div>

                <div>
                  <p className="text-sm text-white/70">Email Support</p>

                  <p className="font-semibold text-[14px] md:text-[17px]">support@confidays.com</p>
                </div>
              </div>

              {/* SAFE */}

              <div
                className="
                  flex
                  items-center
                  gap-4

                  rounded-2xl

                  bg-white/10
                  backdrop-blur-md

                  p-4
                "
              >
                <div
                  className="
                    w-12
                    h-12

                    rounded-xl

                    bg-white/15

                    flex
                    items-center
                    justify-center
                  "
                >
                  <ShieldCheck size={20} />
                </div>

                <div>
                  <p className="text-sm text-white/70">Trusted Care</p>

                  <p className="font-semibold">24/7 Customer Assistance</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* ================= FORM CARD ================= */}

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="
              bg-white/90
              backdrop-blur-xl

              border
              border-pink-100

              rounded-[32px]

              p-7 md:p-9

              shadow-[0_10px_40px_rgba(236,72,153,0.08)]
            "
          >
            {/* HEADING */}

            <div className="mb-8">
              <div
                className="
                  inline-flex
                  items-center
                  gap-2

                  px-4
                  py-2

                  rounded-full

                  bg-pink-100
                  text-pink-500

                  text-xs
                  font-semibold
                  tracking-[2px]
                  uppercase

                  mb-4
                "
              >
                <Headphones size={14} />
                Get In Touch
              </div>

              <h3
                className="
                  text-[28px]
                  md:text-[34px]

                  font-bold

                  text-[#1A237E]

                  mb-3
                "
              >
                Contact Support
              </h3>

              <p
                className="
                  text-gray-500

                  text-[15px]

                  leading-[1.8]
                "
              >
                Fill out the form below and our support team will get back to
                you shortly.
              </p>
            </div>

            {/* FORM */}

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* NAME */}

              <Input
                label="Full Name"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Enter your full name"
              />

              {/* EMAIL */}

              <Input
                label="Email Address"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="Enter your email"
              />

              {/* PHONE */}

              <Input
                label="Phone Number"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="10 digit mobile number"
              />

              {/* MESSAGE */}

              <div>
                <label
                  className="
                    block

                    text-sm
                    font-medium

                    text-[#1A237E]

                    mb-2
                  "
                >
                  Message / Quantity
                </label>

                <textarea
                  rows="4"
                  name="quantity"
                  value={form.quantity}
                  onChange={handleChange}
                  placeholder="Write your message here..."
                  className="
                    w-full

                    rounded-2xl

                    border
                    border-pink-100

                    bg-[#fffafb]

                    px-4
                    py-3

                    text-[15px]

                    outline-none

                    transition-all
                    duration-300

                    focus:border-pink-400
                    focus:ring-4
                    focus:ring-pink-100
                  "
                />
              </div>

              {/* BUTTON */}

              <button
                type="submit"
                disabled={loading}
                className="
                  w-full

                  rounded-full

                  bg-gradient-to-r
                  from-[#1A237E]
                  to-[#EC4899]

                  py-3.5

                  text-white
                  font-semibold

                  shadow-[0_10px_30px_rgba(91,45,168,0.15)]

                  transition-all
                  duration-300

                  hover:scale-[1.01]

                  disabled:opacity-60
                "
              >
                {loading ? "Submitting..." : "Submit Request"}
              </button>

              {/* SUCCESS */}

              {success && (
                <p
                  className="
                    text-green-600
                    text-sm
                    font-medium
                  "
                >
                  {success}
                </p>
              )}

              {/* ERROR */}

              {error && (
                <p
                  className="
                    text-red-500
                    text-sm
                    font-medium
                  "
                >
                  {error}
                </p>
              )}
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ================= INPUT COMPONENT ================= */

function Input({ label, name, value, onChange, placeholder }) {
  return (
    <div>
      <label
        className="
          block

          text-sm
          font-medium

          text-[#1A237E]

          mb-2
        "
      >
        {label}
      </label>

      <input
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required
        className="
          w-full

          rounded-2xl

          border
          border-pink-100

          bg-[#fffafb]

          px-4
          py-3

          text-[15px]

          outline-none

          transition-all
          duration-300

          focus:border-pink-400
          focus:ring-4
          focus:ring-pink-100
        "
      />
    </div>
  );
}